import { useCallback } from 'react';

import { Team } from '@graphql/generated';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';

import { useWebDrawerNavigator } from './useWebDrawerNavigator';

/**
 * TA-10471 QA: clears the web shell's selected-team context for a team the
 * user no longer belongs to (leave, or owner-transfer-then-leave).
 *
 * Removing the team from `listTeams` is not enough. DrawerHeader keeps the
 * team in `selectedTeam` — New Task, Global Search and the team menu all
 * scope off it — and only clears it when getTeam errors. Since that query is
 * now cache-first, a warm entry is returned without a request, no error fires,
 * and the header keeps a team the user has left until a manual team switch or
 * a page reload, causing stale scope and authorization errors.
 *
 * Pair this with `removeTeamFromCache` in the mutation's `update`: this
 * hook fixes the navigation state, that util fixes the cache.
 */
export const useClearTeamContext = () => {
  const { selectedTeam, setSelectedTeam } = useWebDrawerNavigator();

  return useCallback(
    (teamId?: Team['id']) => {
      // Only touch the header's selection when it is the team being left —
      // leaving a non-selected team must not blank out the current scope.
      if (!teamId || selectedTeam?.id !== teamId) return;

      setSelectedTeam(undefined);
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem(SESSION_SELECTED_TEAM_NAME);
      }
    },
    [selectedTeam?.id, setSelectedTeam]
  );
};
